/**
 * The particle workhorse: one pooled simulation and one draw call, shared by nearly every effect.
 *
 * ## Why not `SpriteCloud`
 *
 * `render/pointSprites.ts` already expands points into camera-facing quads, and it is the right
 * tool for *lights* — it is additive, opaque-coloured and has no notion of a particle's age. Smoke
 * is none of those things. Smoke is dark, it has to occlude what is behind it rather than add to
 * it, and its whole visual life is a fade. Adding an alpha path to the light renderer would have
 * meant two blend modes and an age curve inside the module that draws runway edge lights at
 * night, which is how a module stops being about one thing.
 *
 * So this is the other half: same quad expansion, but RGBA, either blend mode, and a pooled
 * simulation in front of it.
 *
 * ## One system, six effects
 *
 * Contrails, missile smoke, wingtip vortices, hit sparks, explosion fireballs and the smoke column
 * that follows them are all "spawn a thing, give it a velocity, watch it grow and fade". They
 * differ in numbers, not in kind, so they share this and are distinguished by the
 * {@link ParticleSpec} handed to {@link ParticleSystem.spawn}. That keeps the per-frame cost to
 * one buffer walk and one upload for all of them, which is what the budget can afford — six
 * separate systems would be six draw calls and six partially-empty buffers.
 *
 * ## Sorting, and the decision not to
 *
 * Alpha-blended particles are order-dependent, and these are not sorted. Sorting a few thousand
 * particles per frame on the CPU costs more than the artefact it fixes: the smoke here is thin,
 * low-contrast and mostly seen against sky, where mis-ordering is invisible. Depth *writing* is
 * off and depth *testing* is on, so particles are correctly hidden by terrain and by the aircraft
 * while remaining unsorted among themselves. That is the standard trade and it is the right one.
 *
 * `updateFrame(alpha, dt)`: yes — {@link ParticleSystem.updateFrame}. Allocation-free after
 * construction; particles come from a fixed pool and a full pool drops the spawn.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Mesh,
  NormalBlending,
  Vector3,
  type PerspectiveCamera,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  attribute,
  cameraProjectionMatrix,
  float,
  length,
  modelViewMatrix,
  smoothstep,
  vec3,
  vec4,
} from 'three/tsl';
import { clamp01 } from '../../core/math.js';

const CORNERS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
] as const;

export type ParticleBlend = 'additive' | 'alpha';

export interface ParticleCloudOptions {
  capacity: number;
  blending: ParticleBlend;
  /**
   * Edge softness. 0 is a hard disc, 1 fades from the centre out. Smoke wants nearly 1; a spark
   * wants a tight core.
   */
  softness?: number;
  name?: string;
}

/**
 * A fixed-capacity set of camera-facing RGBA quads, drawn in one call.
 *
 * The buffer layout mirrors `SpriteCloud`'s deliberately, so the two are easy to read side by
 * side: four vertices per particle, a `corner` attribute for the quad expansion, and everything
 * else duplicated across the four.
 */
export class ParticleCloud {
  readonly mesh: Mesh;
  readonly capacity: number;

  private readonly geometry: BufferGeometry;
  private readonly material: MeshBasicNodeMaterial;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly sizes: Float32Array;
  private countValue = 0;

  constructor(options: ParticleCloudOptions) {
    this.capacity = options.capacity;
    const vertices = this.capacity * 4;

    this.positions = new Float32Array(vertices * 3);
    this.colors = new Float32Array(vertices * 4);
    this.sizes = new Float32Array(vertices);
    const corners = new Float32Array(vertices * 2);
    const indices = new Uint32Array(this.capacity * 6);

    for (let i = 0; i < this.capacity; i++) {
      for (let c = 0; c < 4; c++) {
        corners[(i * 4 + c) * 2] = CORNERS[c][0];
        corners[(i * 4 + c) * 2 + 1] = CORNERS[c][1];
      }
      const base = i * 4;
      indices.set([base, base + 1, base + 2, base, base + 2, base + 3], i * 6);
    }

    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('particleColor', new BufferAttribute(this.colors, 4));
    this.geometry.setAttribute('particleSize', new BufferAttribute(this.sizes, 1));
    this.geometry.setAttribute('corner', new BufferAttribute(corners, 2));
    this.geometry.setIndex(new BufferAttribute(indices, 1));
    this.geometry.boundingSphere = null;
    this.geometry.setDrawRange(0, 0);

    this.material = this.buildMaterial(options.blending, options.softness ?? 0.85);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.name = options.name ?? 'particles';
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    // Particles draw after the opaque world; smoke over fire reads correctly at the same depth.
    this.mesh.renderOrder = options.blending === 'additive' ? 12 : 10;
  }

  private buildMaterial(blending: ParticleBlend, softness: number): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.blending = blending === 'additive' ? AdditiveBlending : NormalBlending;

    const centre = attribute<'vec3'>('position', 'vec3');
    const corner = attribute<'vec2'>('corner', 'vec2');
    const size = attribute<'float'>('particleSize', 'float');
    const colour = attribute<'vec4'>('particleColor', 'vec4');

    const viewPosition = modelViewMatrix.mul(vec4(centre, 1));
    const offset = vec3(corner.x.mul(size), corner.y.mul(size), 0);
    material.vertexNode = cameraProjectionMatrix.mul(vec4(viewPosition.xyz.add(offset), 1));

    /*
     * A soft round falloff, and the reason the quad is never seen.
     *
     * `smoothstep(1, 1−softness, r)` is 1 in the middle and 0 at the corners, so the square the
     * particle is actually made of is cut back to a disc with a feathered edge. Without it every
     * puff of smoke is visibly a rotated square, which is the single most recognisable way for a
     * particle system to look cheap.
     */
    const radial = length(corner);
    const falloff = smoothstep(float(1), float(1 - softness), radial);

    material.colorNode = vec3(colour.xyz);
    material.opacityNode = colour.w.mul(falloff);
    return material;
  }

  set(
    index: number,
    x: number,
    y: number,
    z: number,
    r: number,
    g: number,
    b: number,
    a: number,
    radiusM: number,
  ): void {
    if (index < 0 || index >= this.capacity) return;
    for (let c = 0; c < 4; c++) {
      const v = index * 4 + c;
      this.positions[v * 3] = x;
      this.positions[v * 3 + 1] = y;
      this.positions[v * 3 + 2] = z;
      this.colors[v * 4] = r;
      this.colors[v * 4 + 1] = g;
      this.colors[v * 4 + 2] = b;
      this.colors[v * 4 + 3] = a;
      this.sizes[v] = radiusM;
    }
  }

  /**
   * Publish `count` particles' worth of the buffers.
   *
   * **Only the live prefix is uploaded**, via three's update ranges, and that is not a
   * micro-optimisation. These buffers are sized for the worst case — 3000 particles is 144 KB of
   * positions, 192 KB of colours and 48 KB of sizes — and marking the whole attribute dirty
   * uploads all of it every frame whether forty particles are alive or three thousand. At 60 fps
   * that is tens of megabytes a second of buffer traffic to draw a few puffs of smoke, plus
   * whatever per-upload bookkeeping the backend does on top.
   *
   * Because particles are written into a compacted prefix (`updateFrame` walks the pool and
   * writes the live ones from index 0), the live region is always `[0, count)` and one range
   * covers it.
   */
  commit(count: number): void {
    this.countValue = Math.min(count, this.capacity);
    const vertices = this.countValue * 4;
    this.markRange(this.geometry.getAttribute('position') as BufferAttribute, vertices);
    this.markRange(this.geometry.getAttribute('particleColor') as BufferAttribute, vertices);
    this.markRange(this.geometry.getAttribute('particleSize') as BufferAttribute, vertices);
    this.geometry.setDrawRange(0, this.countValue * 6);
  }

  /** Mark the first `vertices` vertices of an attribute dirty, and nothing beyond them. */
  private markRange(attribute: BufferAttribute, vertices: number): void {
    attribute.clearUpdateRanges();
    if (vertices === 0) {
      // Nothing live: leave it clean rather than uploading a zero-length range.
      attribute.needsUpdate = false;
      return;
    }
    attribute.addUpdateRange(0, vertices * attribute.itemSize);
    attribute.needsUpdate = true;
  }

  get count(): number {
    return this.countValue;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/** How one kind of particle is born, lives and dies. Constant per effect; never allocated. */
export interface ParticleSpec {
  lifetimeS: number;
  /** Radius at birth [m]. */
  startRadiusM: number;
  /** Radius at death [m]. Smoke grows; a spark shrinks. */
  endRadiusM: number;
  /** Linear RGB at birth. Values above 1 bloom. */
  startColour: readonly [number, number, number];
  endColour: readonly [number, number, number];
  startAlpha: number;
  endAlpha: number;
  /** Velocity damping per second, 0…1. 0.9 stops a particle almost immediately. */
  drag: number;
  /** Buoyancy [m/s²], positive up. Fire rises; smoke rises more slowly and for longer. */
  buoyancyMps2: number;
  /** How much of gravity applies — debris 1, smoke 0. */
  gravityScale: number;
}

interface Particle {
  active: boolean;
  readonly position: Vector3;
  readonly velocity: Vector3;
  ageS: number;
  spec: ParticleSpec | null;
  /** Per-particle size jitter, so a burst is not made of identical discs. */
  scale: number;
}

const GRAVITY_RENDER_MPS2 = 9.80665;

/**
 * The pooled simulation in front of a {@link ParticleCloud}.
 *
 * Works entirely in **render space** (y up), not sim NED, because every consumer already has a
 * render-space position to hand — the aircraft's, a target's, an impact point converted once by
 * the caller — and converting per particle per frame would be the most expensive thing in here.
 */
export class ParticleSystem {
  readonly cloud: ParticleCloud;

  private readonly particles: Particle[] = [];
  private cursor = 0;
  private liveValue = 0;
  private droppedValue = 0;

  constructor(options: ParticleCloudOptions) {
    this.cloud = new ParticleCloud(options);
    for (let i = 0; i < options.capacity; i++) {
      this.particles.push({
        active: false,
        position: new Vector3(),
        velocity: new Vector3(),
        ageS: 0,
        spec: null,
        scale: 1,
      });
    }
  }

  get live(): number {
    return this.liveValue;
  }

  /** Spawns refused because the pool was full. A steady climb means the pool is too small. */
  get dropped(): number {
    return this.droppedValue;
  }

  /**
   * Add one particle. Returns false when the pool is full, which callers may ignore.
   *
   * A full pool drops the *newest* particle rather than recycling the oldest. Recycling looks
   * worse: it makes an existing trail flicker out mid-flight, whereas dropping just makes a dense
   * burst slightly less dense, which nobody can see.
   */
  spawn(
    position: Vector3,
    velocity: Vector3,
    spec: ParticleSpec,
    scale = 1,
  ): boolean {
    const particle = this.take();
    if (particle === null) {
      this.droppedValue++;
      return false;
    }
    particle.position.copy(position);
    particle.velocity.copy(velocity);
    particle.ageS = 0;
    particle.spec = spec;
    particle.scale = scale;
    return true;
  }

  private take(): Particle | null {
    const size = this.particles.length;
    for (let scanned = 0; scanned < size; scanned++) {
      const index = this.cursor;
      this.cursor = index + 1 >= size ? 0 : index + 1;
      const particle = this.particles[index];
      if (particle.active) continue;
      particle.active = true;
      this.liveValue++;
      return particle;
    }
    return null;
  }

  /** Move everything, then rebuild the draw buffers. One pass, no allocation. */
  updateFrame(dt: number): void {
    let written = 0;
    for (let i = 0; i < this.particles.length; i++) {
      const particle = this.particles[i];
      if (!particle.active) continue;
      const spec = particle.spec;
      if (spec === null) {
        particle.active = false;
        this.liveValue--;
        continue;
      }

      particle.ageS += dt;
      if (particle.ageS >= spec.lifetimeS) {
        particle.active = false;
        this.liveValue--;
        continue;
      }

      const damping = Math.max(0, 1 - spec.drag * dt);
      particle.velocity.multiplyScalar(damping);
      particle.velocity.y += (spec.buoyancyMps2 - GRAVITY_RENDER_MPS2 * spec.gravityScale) * dt;
      particle.position.addScaledVector(particle.velocity, dt);

      const t = clamp01(particle.ageS / spec.lifetimeS);
      const radius = (spec.startRadiusM + (spec.endRadiusM - spec.startRadiusM) * t) * particle.scale;
      const alpha = spec.startAlpha + (spec.endAlpha - spec.startAlpha) * t;

      this.cloud.set(
        written,
        particle.position.x,
        particle.position.y,
        particle.position.z,
        spec.startColour[0] + (spec.endColour[0] - spec.startColour[0]) * t,
        spec.startColour[1] + (spec.endColour[1] - spec.startColour[1]) * t,
        spec.startColour[2] + (spec.endColour[2] - spec.startColour[2]) * t,
        alpha,
        radius,
      );
      written++;
    }
    this.cloud.commit(written);
  }

  /**
   * Carry every live particle through a floating-origin rebase.
   *
   * Particles are the textbook case from `geodesy.ts`: they have no latitude and longitude of
   * their own, so when the origin moves they must be moved with it or a contrail 8 km long
   * suddenly starts 10 km from the aeroplane that drew it.
   */
  applyOriginShift(deltaX: number, deltaY: number, deltaZ: number): void {
    for (const particle of this.particles) {
      if (!particle.active) continue;
      particle.position.x += deltaX;
      particle.position.y += deltaY;
      particle.position.z += deltaZ;
    }
  }

  /** Kill everything — a respawn, or leaving a mode. */
  clear(): void {
    for (const particle of this.particles) particle.active = false;
    this.liveValue = 0;
    this.cloud.commit(0);
  }

  dispose(): void {
    this.cloud.dispose();
  }
}

/** Metres per pixel at one metre, so callers can keep small effects from vanishing. */
export function metresPerPixel(camera: PerspectiveCamera, viewportHeightPx: number): number {
  const halfFov = ((camera.fov * Math.PI) / 180) / 2;
  return (2 * Math.tan(halfFov)) / Math.max(1, viewportHeightPx);
}
